import { api, setAccessToken } from '@/lib/api/client';

import type {
  AuthUser,
  LoginPayload,
  LoginResponse,
  LoginSuccessResponse,
  MfaStatus,
  RegisterPayload,
  RegisterResponse,
  TrustedDevice,
} from './types';

export async function loginApi(payload: LoginPayload): Promise<LoginResponse> {
  const data = await api.post<LoginResponse>('/auth/login', payload);
  // Seulement appliquer le token si login direct (pas de challenge MFA)
  if (!('requiresMfa' in data) || !data.requiresMfa) {
    setAccessToken((data as LoginSuccessResponse).accessToken);
  }
  return data;
}

export async function registerApi(payload: RegisterPayload): Promise<RegisterResponse> {
  return api.post<RegisterResponse>('/auth/register', payload);
}

export async function logoutApi(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } finally {
    setAccessToken(null);
  }
}

export async function forgotPasswordApi(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email });
}

export async function resetPasswordApi(token: string, password: string): Promise<void> {
  await api.post('/auth/reset-password', { token, password });
}

export async function verifyEmailApi(token: string): Promise<void> {
  await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
}

export async function resendVerificationEmailApi(email: string): Promise<void> {
  await api.post('/auth/resend-verification', { email });
}

export async function getMeApi(): Promise<AuthUser> {
  return api.get<AuthUser>('/users/me');
}

/** Échange le refresh token (cookie) contre un nouvel access token.
 *  Utile après une promotion de rôle pour obtenir un JWT à jour. */
export async function refreshAccessTokenApi(): Promise<{ accessToken: string }> {
  return api.post<{ accessToken: string }>('/auth/refresh');
}

// ─── MFA ────────────────────────────────────────────────────────

export interface MfaVerifyDto {
  challengeToken: string;
  method: string;
  code: string;
  trustDevice?: boolean;
  deviceToken?: string;
  deviceLabel?: string;
}

export async function mfaVerifyApi(dto: MfaVerifyDto): Promise<LoginSuccessResponse> {
  return api.post<LoginSuccessResponse>('/auth/2fa/verify', dto);
}

export async function mfaResendApi(challengeToken: string): Promise<void> {
  await api.post('/auth/2fa/resend', { challengeToken });
}

export async function getMfaStatusApi(): Promise<MfaStatus> {
  return api.get<MfaStatus>('/auth/2fa/status');
}

export interface TotpSetupResponse {
  otpauthUri: string;
  qrCodeDataUrl: string;
  secret: string;
}

export async function setupTotpApi(): Promise<TotpSetupResponse> {
  return api.get<TotpSetupResponse>('/auth/2fa/setup/totp');
}

export async function enableTotpApi(code: string): Promise<{ backupCodes: string[] }> {
  return api.post<{ backupCodes: string[] }>('/auth/2fa/enable', { code });
}

export async function disableTotpApi(): Promise<void> {
  await api.post('/auth/2fa/disable');
}

export async function regenerateBackupCodesApi(): Promise<{ backupCodes: string[] }> {
  return api.post<{ backupCodes: string[] }>('/auth/2fa/backup-codes/regenerate');
}

export async function getTrustedDevicesApi(): Promise<TrustedDevice[]> {
  return api.get<TrustedDevice[]>('/auth/trusted-devices');
}

export async function revokeTrustedDeviceApi(id: number): Promise<void> {
  await api.delete(`/auth/trusted-devices/${id}`);
}

export async function revokeAllTrustedDevicesApi(): Promise<void> {
  await api.delete('/auth/trusted-devices');
}

export async function updateSecuritySettingsApi(dto: { trustedDeviceTtlDays: number }): Promise<void> {
  await api.patch('/auth/security-settings', dto);
}

// ─── IMPERSONATION ──────────────────────────────────────────────

export interface ImpersonateResponse {
  accessToken: string;
  user: AuthUser;
  impersonating: true;
}

export async function impersonateApi(userId: number): Promise<ImpersonateResponse> {
  return api.post<ImpersonateResponse>(`/auth/impersonate/${userId}`, {});
}
